// React
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import {
  Card,
  CircularProgress,
  Collapse,
  Fade,
  Grid,
  Icon,
  Modal,
  Tooltip,
} from "@mui/material";

// Custom UI components
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { UserContext } from "context/user";
import { PERMISOS_COMPRAS, tienePermisoCompras } from "layouts/compras/permisos";
import { ConfirmarContext } from "context/ConfirmarContext";
import { formatDate } from "utils/formatters";
import { getUproColor } from "utils/colors";
import { formatMoneyPunto } from "utils/formatters";
import { recortarTexto } from "utils/formatters";
import DinamicModal from "components/Modals/DinamicModal";
import { mensajeError } from "layouts/compras/mensajeError";
import { nombreCompraDetalle } from "utils/presentacionCompra";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "70%", md: "60%", xxl: "50%" },
  overflow: "auto",
  p: 3,
};

export default function ModalProvisionSeleccion({
  open,
  handleClose,
  idPedido,
  refetchParent,
  productosSinProvision,
}) {
  const { handleSnackbar } = useContext(MessageManager);
  const { user, verificarAcceso } = useContext(UserContext);
  const puedeGenerar = tienePermisoCompras(user, verificarAcceso, PERMISOS_COMPRAS.GENERAR_ORDENES);
  const { confirmar } = useContext(ConfirmarContext);
  const [data, setData] = useState({});
  const [contracted, setContracted] = useState(["presentadas"]);
  const [cotizacionSelection, setCotizacionSelection] = useState([]);
  const [detallesData, setDetallesData] = useState(null);
  const [detalles, setDetalles] = useState([]);
  const [cotizacionServicio, setCotizacionServicio] = useState(false);

  const [getPedido, { loading, data: pedido }] = useLazyQuery(
    gql`
      query getPedidoCompraModalProvisionSeleccion($id: ID!) {
        pedidosCompra(filter: { id: $id }) {
          id
          licitacionEstado
          licitacionInicio
          licitacionFin
          categorias {
            id
            nombre
          }
          cotizaciones {
            id
            createdAt
            proveedor {
              id
              razonSocial
              nombre
              apellido
            }
            detalles {
              id
            }
          }
          detalles {
            id
            numero
            nombreCompra
            unidadNombre
            unidadAbreviatura
            cantidad
            totalMP
            requierePack
            unidadPack
            materiaPrima {
              id
              nombre
              nombreCompra
              tipo
            }
            cantidadVerificada
            cotizaciones {
              id
              precio
              materiaPrimaAlternativo
              nombreAlternativo
              observacion
              plazoEntrega
            }
            cotizacionSeleccionada {
              id
            }
            cotizacionSeleccionadaDesc
            nombreServicio
          }
        }
      }
    `,
    {
      fetchPolicy: "network-only",
    }
  );

  const [generarProvisiones, { loading: loadingSeleccion }] = useMutation(
    gql`
      mutation generarProvisiones(
        $idPedidoCompra: ID!
        $detalleSeleccion: [SeleccionCotizacionInput]!
        $servicio: Boolean
      ) {
        generarProvisiones(
          idPedidoCompra: $idPedidoCompra
          detalleSeleccion: $detalleSeleccion
          servicio: $servicio
        ) {
          id
        }
      }
    `,
    {
      onCompleted: () => {
        refetchParent();
        handleClose();
        handleSnackbar("Selección de cotizaciones guardada", "success");
      },
      onError: (error) => {
        handleSnackbar(mensajeError(error, "No se pudieron generar las órdenes de provisión"), "error");
      },
    }
  );

  useEffect(() => {
    if (open && idPedido) {
      getPedido({ variables: { id: idPedido } }).then((data) => {
        let pedido = data?.data?.pedidosCompra?.[0];
        if (pedido) {
          setData({
            estado: pedido.licitacionEstado,
          });

          let selection = [];

          const pendientesPorDetalle = new Map(
            productosSinProvision.map((detalle) => [
              String(detalle.id),
              detalle.cantidadPendienteProvision,
            ])
          );
          let detalles = pedido.detalles
            .filter((d) => pendientesPorDetalle.has(String(d.id)))
            .map((detalle) => ({
              ...detalle,
              cantidadVerificada:
                pendientesPorDetalle.get(String(detalle.id)) ?? detalle.cantidadVerificada,
            }));

          setDetalles(detalles);

          let selection_exists = detalles.some((d) => Boolean(d.cotizacionSeleccionada));

          if (selection_exists) {
            selection = detalles.map((d) => ({
              ...d,
              cotizacion:
                (d.cotizacionSeleccionada &&
                  d.cotizaciones.find((c) => c.id == d.cotizacionSeleccionada.id)) ||
                {},
            }));
          } else if (pedido.licitacionEstado == "Cerrada") {
            selection = detalles
              .filter((d) => d.cotizaciones?.length > 0)
              .map((detalle) => {
                return {
                  ...detalle,
                  cotizacion: detalle.cotizaciones.reduce(
                    (prev, current) => (prev.precio < current.precio ? prev : current),
                    {}
                  ),
                };
              });
          }

          setCotizacionSelection(
            selection.map((detalle) => ({
              detalle: detalle,
              cotizacion: detalle.cotizacion,
              observacion: detalle.cotizacionSeleccionadaDesc,
            }))
          );
        }
      });
    } else {
      setData({});
    }
  }, [idPedido, open, productosSinProvision]);

  const handleFinalizar = () => {
    confirmar({
      title: "Finalizar cotización",
      message:
        "Se finalizará la cotización y se generarán las órdenes de provisión. El costo del catálogo se actualizará únicamente al confirmar la recepción del remito, por unidad base (precio por pack dividido por su equivalencia). Luego deberá enviar las órdenes a los proveedores desde cada orden. ¿Desea continuar?",
      func: () => {
        let input = {};
        input.idPedidoCompra = idPedido;
        input.detalleSeleccion = cotizacionSelection.map((c) => ({
          idCotizacionDetalle: c.cotizacion?.id,
          idPedidoCompraDetalle: c.detalle.id,
          observacion: c.observacion,
        }));
        input.servicio = cotizacionServicio;

        generarProvisiones({ variables: input });
      },
    });
  };
  const getIcon = (tipo) => {
    let icon = {};
    switch (tipo) {
      case "Material":
        icon = { name: "category", color: "uproYellow" };
        break;
      case "Consumible":
        icon = { name: "recycling", color: "uproGreen" };
        break;
      default:
        icon = { name: "layers", color: "uproPink" };
    }

    return (
      <Icon fontSize="medium" sx={{ color: getUproColor(icon.color) }}>
        {icon.name}
      </Icon>
    );
  };
  useEffect(() => {
    if (open) {
      detalles?.find((detalle) => detalle.nombreServicio)
        ? setCotizacionServicio(true)
        : setCotizacionServicio(false);
    } else {
      setCotizacionServicio(false);
    }
  }, [detalles]);

  const contract = (name) => {
    if (contracted.some((c) => c == name)) {
      setContracted((prev) => prev.filter((c) => c != name));
    } else {
      setContracted((prev) => prev.concat(name));
    }
  };

  const handleCheck = (e, cotizacion, detalle) => {
    if (cotizacionSelection.some((c) => c.cotizacion?.id == cotizacion.id)) {
      confirmar({
        title: "Motivo de deselección",
        message: "Debe ingresar un motivo de deselección",
        pedirMotivo: true,
        placeholder: "Ingrese motivo",
        func: (motivo) => {
          setCotizacionSelection((prev) =>
            prev.filter((c) => c.cotizacion?.id != cotizacion.id).concat({
              detalle: detalle,
              cotizacion: null,
              observacion: motivo,
            })
          );
        }
      });
      return;
    };

    //get cotizacion mas barata para ese detalle
    let mejorCotizacion = detalle.cotizaciones.reduce(
      (prev, current) => (prev.precio < current.precio ? prev : current),
      {}
    );
    if (cotizacion.id != mejorCotizacion.id) {
      confirmar({
        title: "Motivo de elección",
        message: "La cotización seleccionada no es la más barata, ingrese motivo de elección",
        pedirMotivo: true,
        placeholder: "Ingrese motivo",
        func: (motivo) => {
          setCotizacionSelection((prev) =>
            prev
              .filter((c) => c.detalle.id != detalle.id)
              .concat({
                detalle: detalle,
                cotizacion: cotizacion,
                observacion: motivo,
              })
          );
        },
      });
    } else {
      setCotizacionSelection((prev) =>
        prev
          .filter((c) => c.detalle.id != detalle.id)
          .concat({
            detalle: detalle,
            cotizacion: cotizacion,
            observacion: null,
          })
      );
    }
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ zIndex: 99 }}
          >
            <SoftBox display="flex" gap={1}>
              <Icon fontSize="small">flag</Icon>
              <SoftTypography variant="h6">Generar ordenes de provisión</SoftTypography>
            </SoftBox>
            <SoftBox display="flex" justifyContent="end" alignItems="center" gap={1}>
              <Tooltip title="Cerrar" placement="top">
                <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                  close
                </Icon>
              </Tooltip>
            </SoftBox>
          </SoftBox>
          <Grid container spacing={2}>
            {data.estado !== "No iniciada" && (
              <Grid item xs={12}>
                <SoftBox display="flex" alignItems="center" py={1} sx={{ cursor: "pointer" }}>
                  <SoftTypography
                    variant="caption"
                    fontWeight="bold"
                    onClick={() => contract("presentadas")}
                  >
                    {loading ? 'Cargando cotizaciones...' : `Cotizaciones presentadas (${pedido?.pedidosCompra?.[0]?.cotizaciones.length || 0})`}
                  </SoftTypography>
                  <Icon color="secondary">
                    {contracted.some((c) => c == `presentadas`)
                      ? "keyboard_arrow_down"
                      : "keyboard_arrow_up"}
                  </Icon>
                </SoftBox>
                <Collapse in={!contracted.some((c) => c == `presentadas`)}>
                  {["Finalizada", "Cerrada"].includes(data.estado) ? (
                    <SoftBox bgColor="grey-100" borderRadius="0.5rem">
                      {pedido?.pedidos?.[0]?.cotizaciones.map((item, index) => (
                        <SoftBox
                          key={index}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          borderBottom={
                            index < pedido?.pedidos?.[0]?.cotizaciones.length - 1
                              ? "1px solid #e6e6e6"
                              : "none"
                          }
                          p={1}
                        >
                          <SoftBox display="flex" alignItems="center" gap={1}>
                            <Icon fontSize="small">person</Icon>
                            <SoftTypography variant="caption" fontWeight="bold">
                              {[
                                item?.proveedor.razonSocial,
                                item?.proveedor.nombre,
                                item?.proveedor.apellido,
                              ].join(" ")}
                            </SoftTypography>
                          </SoftBox>
                          <SoftTypography variant="caption">
                            {formatDate(item.createdAt, "dd/MM/yyyy hh:mm")}
                          </SoftTypography>
                          <SoftTypography variant="caption">
                            {item.detalles?.length} producto{item.detalles?.length == 1 ? "" : "s"}
                          </SoftTypography>
                          {/* <SoftButton variant="text" color="uproGreen">
                            Ver cotización
                          </SoftButton> */}
                        </SoftBox>
                      ))}
                    </SoftBox>
                  ) : (
                    <SoftBox display="flex" justifyContent="center" alignItems="center" gap={1}>
                      <Icon color="secondary">visibility_off</Icon>
                      <SoftTypography variant="caption" color="secondary">
                        No se pueden visualizar las cotizaciones hasta que el porceso esté cerrado
                      </SoftTypography>
                    </SoftBox>
                  )}
                </Collapse>
              </Grid>
            )}
          </Grid>

          {["Finalizada", "Cerrada"].includes(data.estado) && (
            <SoftBox>
              <SoftTypography variant="caption" fontWeight="bold">
                Elegir cotizaciones
              </SoftTypography>
              <SoftBox
                bgColor="grey-100"
                borderRadius="0.5rem"
                maxHeight="25vh"
                sx={{ overflowY: "scroll" }}
              >
                {detalles.map((item, index) => {
                  let observacion = cotizacionSelection.find((s) => s.detalle.id == item.id && !s.cotizacion?.id)
                    ?.observacion;
                  return (
                    <SoftBox key={index}>
                      <Grid
                        container
                        p={1}
                        onClick={() => contract(`detalle-${item.id}`)}
                        sx={{ cursor: "pointer" }}
                        borderTop={index ? "1px solid #e6e6e6" : "none"}
                      >
                        <Grid item xs={1.5} display="flex" alignItems="center" gap={1}>
                          {getIcon(item.materiaPrima?.tipo)}
                          <SoftTypography variant="caption">
                            {item.materiaPrima?.id || item.id}
                          </SoftTypography>
                        </Grid>
                        <Grid item xs={9.5} display="flex" alignItems="center" gap={1}>
                          <SoftTypography variant="caption" fontWeight="bold">
                            {nombreCompraDetalle(item)}
                          </SoftTypography>
                          {observacion && (
                            <Tooltip title={observacion}>
                              <SoftBox display="flex" alignItems="center">
                                <Icon color="secondary" fontSize="small">
                                  edit_note
                                </Icon>
                                <SoftTypography variant="caption" color="secondary">
                                  {recortarTexto(observacion, 30)}
                                </SoftTypography>
                              </SoftBox>
                            </Tooltip>
                          )}
                        </Grid>
                        <Grid item xs={0.5} textAlign="right">
                          <SoftTypography variant="caption">{item.numero}</SoftTypography>
                        </Grid>
                        <Grid
                          item
                          xs={0.5}
                          display="flex"
                          alignItems="center"
                          justifyContent="flex-end"
                        >
                          <Icon color="secondary">
                            {contracted.some((c) => c == `detalle-${item.id}`)
                              ? "keyboard_arrow_down"
                              : "keyboard_arrow_up"}
                          </Icon>
                        </Grid>
                      </Grid>
                      <Collapse in={!contracted.some((c) => c == `detalle-${item.id}`)}>
                        {item.cotizaciones?.map((cotizacion, j) => {
                          let proveedor = pedido?.pedidosCompra?.[0]?.cotizaciones.find((c) =>
                            c.detalles.some((d) => d.id == cotizacion.id)
                          )?.proveedor;
                          let selection = cotizacionSelection.find(
                            (c) => c.cotizacion?.id == cotizacion.id
                          );
                          let checked = Boolean(selection);
                          let mejorPrecio =
                            item.cotizaciones.reduce(
                              (prev, current) => (prev.precio < current.precio ? prev : current),
                              {}
                            ).id == cotizacion.id;
                          let observacion = selection?.observacion;
                          return (
                            <SoftBox key={j} bgColor="white">
                              <Grid container p={1} borderTop="1px solid #e6e6e6">
                                <Grid
                                  item
                                  xs={1}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  <Icon
                                    sx={{ cursor: "pointer" }}
                                    onClick={(e) => handleCheck(e, cotizacion, item)}
                                  >
                                    {checked ? "check_box" : "check_box_outline_blank"}
                                  </Icon>
                                </Grid>
                                <Grid item xs={6.5} display="flex" alignItems="center" gap={1}>
                                  <Icon fontSize="small">person</Icon>
                                  <SoftTypography
                                    variant="caption"
                                    fontWeight={checked ? "bold" : "regular"}
                                  >
                                    {[
                                      proveedor?.razonSocial,
                                      proveedor?.nombre,
                                      proveedor?.apellido,
                                    ].join(" ")}
                                  </SoftTypography>
                                  {mejorPrecio && (
                                    <Tooltip title="Mejor precio">
                                      <Icon color="success">keyboard_double_arrow_down</Icon>
                                    </Tooltip>
                                  )}
                                  {cotizacion.materiaPrimaAlternativo && (
                                    <Tooltip title="Producto alternativo">
                                      <Icon color="primary">swap_horizontal</Icon>
                                    </Tooltip>
                                  )}
                                  {observacion && (
                                    <Tooltip title={observacion}>
                                      <SoftBox display="flex" alignItems="center">
                                        <Icon color="secondary" fontSize="small">
                                          edit_note
                                        </Icon>
                                        <SoftTypography variant="caption" color="secondary">
                                          {recortarTexto(observacion, 30)}
                                        </SoftTypography>
                                      </SoftBox>
                                    </Tooltip>
                                  )}
                                </Grid>
                                <Grid
                                  item
                                  xs={1.5}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="flex-end"
                                >
                                  <SoftTypography
                                    variant="caption"
                                    fontWeight={checked ? "bold" : "regular"}
                                  >
                                    $ {formatMoneyPunto(cotizacion.precio)} / {item.requierePack ? item.unidadPack : item.unidadAbreviatura}
                                  </SoftTypography>
                                </Grid>
                                <Grid
                                  item
                                  xs={1}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="flex-end"
                                >
                                  <SoftTypography
                                    variant="caption"
                                    fontWeight={checked ? "bold" : "regular"}
                                  >
                                    {item.cantidadVerificada} {item.requierePack ? item.unidadPack : item.unidadAbreviatura}
                                  </SoftTypography>
                                </Grid>
                                <Grid
                                  item
                                  xs={1.5}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="flex-end"
                                >
                                  <SoftTypography
                                    variant="caption"
                                    fontWeight={checked ? "bold" : "regular"}
                                  >
                                    $ {formatMoneyPunto(cotizacion.precio * item.cantidadVerificada)}
                                  </SoftTypography>
                                </Grid>
                                <Grid
                                  item
                                  xs={0.5}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="flex-end"
                                >
                                  <Tooltip title="Ver detalles">
                                    <Icon
                                      sx={{ cursor: "pointer" }}
                                      onClick={() =>
                                        setDetallesData({ cotizacion, detalle: item, proveedor })
                                      }
                                      color="secondary"
                                    >
                                      info
                                    </Icon>
                                  </Tooltip>
                                </Grid>
                              </Grid>
                            </SoftBox>
                          );
                        })}
                      </Collapse>
                    </SoftBox>
                  )
                })}
              </SoftBox>
              <SoftBox bgColor="grey-100" borderRadius="0.5rem" mt={1}>
                <Grid container px={1} py={0.5}>
                  <Grid item xs={6}>
                    <SoftTypography variant="button" fontWeight="bold">
                      Total
                    </SoftTypography>
                  </Grid>
                  <Grid item xs={6} display="flex" alignItems="center" justifyContent="flex-end">
                    <SoftTypography variant="button" fontWeight="bold">
                      ${" "}
                      {formatMoneyPunto(
                        cotizacionSelection
                          .filter((c) => c.cotizacion?.id)
                          .reduce(
                            (prev, current) =>
                              prev + current.cotizacion.precio * current.detalle.cantidadVerificada,
                            0
                          ) || 0
                      )}
                    </SoftTypography>
                  </Grid>
                </Grid>
              </SoftBox>
            </SoftBox>
          )}

          {puedeGenerar && cotizacionSelection.length > 0 ? (
            <SoftButton color="uproGreen" circular sx={{ marginTop: 2 }} onClick={handleFinalizar}>
              {loadingSeleccion ? (
                <CircularProgress color="white" size={17} />
              ) : (
                <Icon>done_all</Icon>
              )}
              &nbsp;finalizar y generar op
            </SoftButton>
          ) : null}

          <DinamicModal
            open={!!detallesData}
            handleClose={() => setDetallesData(null)}
            title="Detalle de cotización"
            data={{
              Proveedor: [
                detallesData?.proveedor?.razonSocial,
                detallesData?.proveedor?.nombre,
                detallesData?.proveedor?.apellido,
              ].join(" "),
              Producto: detallesData?.cotizacion?.materiaPrimaAlternativo
                ? detallesData?.cotizacion?.nombreAlternativo
                : nombreCompraDetalle(detallesData?.detalle),
              Alternativo: detallesData?.cotizacion?.materiaPrimaAlternativo ? "Si" : "No",
              "Precio unitario": `$ ${formatMoneyPunto(detallesData?.cotizacion?.precio || 0)}`,
              Cantidad: `${detallesData?.detalle?.cantidadVerificada || 0} ${detallesData?.detalle?.requierePack ? detallesData?.detalle?.unidadPack : detallesData?.detalle?.unidadAbreviatura || ""}`,
              "Unidad del precio": detallesData?.detalle?.requierePack ? detallesData?.detalle?.unidadPack : detallesData?.detalle?.unidadNombre,
              Equivalencia: detallesData?.detalle?.requierePack ? `1 ${detallesData?.detalle?.unidadPack} = ${Number(detallesData?.detalle?.totalMP) / Number(detallesData?.detalle?.cantidad)} ${detallesData?.detalle?.unidadAbreviatura || ""}` : "Unidad del catálogo",
              Total: `$ ${formatMoneyPunto(
                detallesData?.cotizacion?.precio * detallesData?.detalle?.cantidadVerificada || 0
              )}`,
              "Plazo de Entrega": detallesData?.cotizacion?.plazoEntrega +" "+ "Días Corridos",
              Observación: detallesData?.cotizacion?.observacion || "Sin observaciones",
            }}
          />
        </Card>
      </Fade>
    </Modal>
  );
}

ModalProvisionSeleccion.defaultProps = {
  refetchParent: () => { },
};

ModalProvisionSeleccion.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  idPedido: PropTypes.string,
  refetchParent: PropTypes.func,
  productosSinProvision: PropTypes.array,
};
